import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useOrg } from "@/lib/orgContext";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Users, MoreHorizontal, Pencil, Trash2, Search, Building2, Mail, Phone } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";

interface Counterparty {
  id: number;
  name: string;
  firm: string | null;
  role: string;
  email: string;
  phone: string | null;
  notes: string | null;
  createdAt: string;
}

const ROLE_LABELS: Record<string, string> = {
  coach: "Coach",
  agent: "Agent",
  attorney: "Attorney",
  other: "Other",
};

const ROLE_COLORS: Record<string, string> = {
  coach: "bg-blue-50 text-blue-700 border-blue-200",
  agent: "bg-purple-50 text-purple-700 border-purple-200",
  attorney: "bg-amber-50 text-amber-700 border-amber-200",
  other: "bg-gray-50 text-gray-700 border-gray-200",
};

const EMPTY_FORM = { name: "", firm: "", role: "agent" as string, email: "", phone: "", notes: "" };

export default function Counterparties() {
  const { getToken } = useAuth();
  const { org } = useOrg();
  const { toast } = useToast();
  const isAdmin = org?.role === "admin";

  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Counterparty | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [isSaving, setIsSaving] = useState(false);

  const fetchCounterparties = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch("/api/counterparties", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCounterparties(await res.json());
    } catch { /* ignore */ }
    setIsLoading(false);
  }, [getToken]);

  useEffect(() => { fetchCounterparties(); }, [fetchCounterparties]);

  const openNew = () => {
    setEditTarget(null);
    setFormData({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (cp: Counterparty) => {
    setEditTarget(cp);
    setFormData({ name: cp.name, firm: cp.firm ?? "", role: cp.role, email: cp.email, phone: cp.phone ?? "", notes: cp.notes ?? "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast({ title: "Missing fields", description: "Name and email are required.", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    try {
      const token = await getToken();
      const body = {
        name: formData.name.trim(),
        firm: formData.firm.trim() || null,
        role: formData.role,
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        notes: formData.notes.trim() || null,
      };
      const url = editTarget ? `/api/counterparties/${editTarget.id}` : `/api/counterparties`;
      const method = editTarget ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Save failed");
      }
      toast({ title: editTarget ? "Counterparty updated" : "Counterparty added" });
      setDialogOpen(false);
      fetchCounterparties();
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not save counterparty" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (cp: Counterparty) => {
    if (!confirm(`Remove ${cp.name} from the directory?`)) return;
    try {
      const token = await getToken();
      await fetch(`/api/counterparties/${cp.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Counterparty removed" });
      fetchCounterparties();
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not remove counterparty" });
    }
  };

  const filtered = counterparties.filter((cp) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      cp.name.toLowerCase().includes(q) ||
      cp.email.toLowerCase().includes(q) ||
      (cp.firm?.toLowerCase().includes(q) ?? false) ||
      cp.role.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Counterparty Directory</h1>
          <p>Manage coaches, agents, and attorneys involved in contract negotiations.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Counterparty
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, firm, or email…"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No counterparties yet"
              description={isAdmin ? "Add coaches, agents, and attorneys to quickly fill in the Send dialog." : "No counterparties have been added yet."}
              action={isAdmin ? { label: "Add Counterparty", onClick: openNew } : undefined}
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Firm / Affiliation</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      {isAdmin && <TableHead className="w-10" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((cp) => (
                      <TableRow key={cp.id} className="group">
                        <TableCell className="font-medium">{cp.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {cp.firm ? (
                            <span className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5" />
                              {cp.firm}
                            </span>
                          ) : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${ROLE_COLORS[cp.role] ?? ""}`}>
                            {ROLE_LABELS[cp.role] ?? cp.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <a href={`mailto:${cp.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {cp.email}
                          </a>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {cp.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {cp.phone}
                            </span>
                          ) : "—"}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(cp)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(cp)}>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((cp) => (
                  <div key={cp.id} className="rounded-xl border p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-sm">{cp.name}</p>
                        {cp.firm && <p className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" />{cp.firm}</p>}
                      </div>
                      <Badge variant="outline" className={`text-xs ${ROLE_COLORS[cp.role] ?? ""}`}>
                        {ROLE_LABELS[cp.role] ?? cp.role}
                      </Badge>
                    </div>
                    <a href={`mailto:${cp.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <Mail className="h-3 w-3" />{cp.email}
                    </a>
                    {cp.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{cp.phone}</p>}
                    {isAdmin && (
                      <div className="flex gap-2 pt-1">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openEdit(cp)}>
                          <Pencil className="h-3 w-3 mr-1" />Edit
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-destructive" onClick={() => handleDelete(cp)}>
                          <Trash2 className="h-3 w-3 mr-1" />Remove
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!isSaving) setDialogOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editTarget ? "Edit Counterparty" : "Add Counterparty"}</DialogTitle>
            <DialogDescription>
              {editTarget ? "Update the details for this contact." : "Add a coach, agent, or attorney to your negotiation directory."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input placeholder="Full name" value={formData.name} onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Firm / Affiliation</Label>
              <Input placeholder="Agency, law firm, university…" value={formData.firm} onChange={(e) => setFormData((f) => ({ ...f, firm: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData((f) => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-destructive">*</span></Label>
              <Input type="email" placeholder="contact@example.com" value={formData.email} onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input placeholder="+1 555 000 0000" value={formData.phone} onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea placeholder="Any context about this contact…" rows={2} value={formData.notes} onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving…" : editTarget ? "Save Changes" : "Add Counterparty"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
